import { useEffect, useMemo, useState } from 'react';
import { categorizeActivity } from '../utils/networkHealth';

const EVENT_NAME = 'genogram:network-members-changed';

const normalizeMember = (entry) => {
  if (!entry) {
    return null;
  }

  const person = entry.rawPerson || entry.person || {};
  const fallbackId = person.id || entry.id || entry._id || null;
  const name = entry.name || person.name || 'Unnamed person';
  const role = entry.role || person.role || '';
  const relationship = entry.relationshipToChild || person.relationshipToChild || '';
  const lastContactAt = entry.lastContactAt || person.lastContactAt || person.networkLastContactAt || null;
  const notes = entry.notes || person.networkNotes || person.notes || '';
  const sourceProvenance = Array.isArray(entry.sourceProvenance)
    ? entry.sourceProvenance
    : ['genogram'];

  return {
    _id: entry._id || fallbackId || `genogram-${name}`,
    name,
    role,
    relationshipToChild: relationship,
    lastContactAt,
    notes,
    sourceProvenance,
    phones: entry.phones || person.phones || [],
    emails: entry.emails || person.emails || [],
    addresses: entry.addresses || person.addresses || [],
    commitmentLevel: entry.commitmentLevel || person.commitmentLevel || 'unknown',
    childComfort: entry.childComfort || person.childComfort || 'unknown',
    caregiverOpenness: entry.caregiverOpenness || person.caregiverOpenness || 'unknown'
  };
};

const buildActivityMap = (members) => {
  const map = new Map();
  members.forEach((member) => {
    map.set(member._id, categorizeActivity(member));
  });
  return map;
};

const prepareState = (snapshot) => {
  if (!snapshot || !Array.isArray(snapshot.members)) {
    return { members: [], activityByMember: new Map() };
  }

  const members = snapshot.members
    .map((entry) => normalizeMember(entry))
    .filter(Boolean);

  return {
    members,
    activityByMember: buildActivityMap(members)
  };
};

const useGenogramNetworkMembers = () => {
  const [snapshot, setSnapshot] = useState(() => {
    if (typeof window === 'undefined') {
      return { members: [], activityByMember: new Map() };
    }
    return prepareState(window.__genogramNetworkState);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handler = (event) => {
      setSnapshot(prepareState(event.detail));
    };

    window.addEventListener(EVENT_NAME, handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
    };
  }, []);

  const value = useMemo(() => snapshot, [snapshot]);
  return value;
};

export default useGenogramNetworkMembers;
